import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useChatPanelStore from 'stateManagement/store/useChatPanelStore';

import { actionHandlers } from '../../../../stateManagement/redux/actionPlans/actionHandler';
import {
  selectActionPlanById,
  selectActionPlans,
} from '../../../../stateManagement/redux/actionPlans/selector';
import {
  addActionPlanLog,
  resetActionPlan,
  updateActionPlanStatus,
  updateCurrentIndex,
} from '../../../../stateManagement/redux/actionPlans/slice';
import { ACTION_PLAN_STATUSES } from '../../../../stateManagement/redux/actionPlans/types';

const ActionDetails = ({ actions }) => {
  return (
    <div className='mt-4'>
      <h5 className=''>Actions in Plan:</h5>
      {actions.map((action, index) => (
        <div key={index} className='alert alert-info'>
          <strong>Action {index + 1}:</strong>
          <br />
          {action.type}
          <br />
          <small className='text-muted'>
            Parameters: {JSON.stringify(action.params)}
          </small>
        </div>
      ))}
    </div>
  );
};

const ExecutionLogs = ({ logs }) => {
  if (!logs || logs.length === 0) return null;

  return (
    <div className='mt-4'>
      <div className='fw-semibold mb-2'>Execution Log:</div>
      {logs.map((log, index) => (
        <div
          key={index}
          className={`alert ${
            log.status === 'Success' ? 'alert-default' : 'alert-danger'
          }`}
        >
          <div className='d-flex justify-content-between align-items-start'>
            <div>
              <strong>Action {log.index + 1}:</strong>
              <br />
              <small className='text-muted'>Timestamp: {log.timestamp}</small>
              <br />
              <small className='text-muted'>Status: {log.status}</small>
            </div>
          </div>

          {log.error && (
            <div className='mt-2 text-danger'>Error: {log.error}</div>
          )}

          {log.screenshot && (
            <div className='mt-4'>
              <img
                src={log.screenshot}
                alt={`Screenshot of action ${log.index + 1}`}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const ActionPlanViewer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showActionDetails, setShowActionDetails] = useState(false);
  const [showExecutionLogs, setShowExecutionLogs] = useState(true);

  const chatSessionId = useChatPanelStore((state) => state.chatSessionId);

  const plans = useSelector(selectActionPlans);
  const selectedPlan = useSelector((state) =>
    selectActionPlanById(state, selectedPlanId)
  );

  const executeAction = async (action) => {
    const handler = actionHandlers[action.type];
    if (!handler) {
      throw new Error(`No handler found for action type: ${action.type}`);
    }

    return handler({ action, navigate });
  };

  const navigateThroughPlan = async () => {
    if (!selectedPlan) return;

    dispatch(resetActionPlan({ id: selectedPlan.id }));
    dispatch(
      updateActionPlanStatus({
        id: selectedPlan.id,
        status: ACTION_PLAN_STATUSES.STARTED,
      })
    );

    for (let i = 0; i < selectedPlan.sherlockActions.length; i++) {
      try {
        setIsLoading(true);
        dispatch(updateCurrentIndex({ id: selectedPlan.id, index: i }));

        const action = selectedPlan.sherlockActions[i];

        // TODO - 2 - UPDATE this to get data from the execute or screenshot depending on what the handler did, then attach to the dispatch below
        const data = await executeAction(action);

        // add to redux
        dispatch(
          addActionPlanLog({
            id: selectedPlan.id,
            log: {
              index: i,
              action: action,
              timestamp: new Date().toLocaleString(),
              status: 'Success',
              screenshot: data.screenshot ? data.screenshot : null,
              rawData: data.rawData ? data.rawData : null,
            },
          })
        );
      } catch (error) {
        dispatch(
          addActionPlanLog({
            id: selectedPlan.id,
            log: {
              index: i,
              action: selectedPlan.sherlockActions[i],
              timestamp: new Date().toLocaleString(),
              status: 'Failed',
              error: error.message,
            },
          })
        );
        dispatch(
          updateActionPlanStatus({
            id: selectedPlan.id,
            status: ACTION_PLAN_STATUSES.FAILED,
          })
        );
        break;
      } finally {
        setIsLoading(false);
      }
    }

    if (selectedPlan.status !== ACTION_PLAN_STATUSES.FAILED) {
      dispatch(
        updateActionPlanStatus({
          id: selectedPlan.id,
          status: ACTION_PLAN_STATUSES.COMPLETED,
        })
      );
    }
  };

  // Handle plan selection and automatic execution
  useEffect(() => {
    if (plans.length > 0 && chatSessionId) {
      const newPlanId = plans.find(
        (p) =>
          p.chatSessionId === chatSessionId &&
          p.status !== ACTION_PLAN_STATUSES.COMPLETED
      )?.id;

      if (newPlanId && newPlanId !== selectedPlanId) {
        setSelectedPlanId(newPlanId);
      }
    }
  }, [plans, chatSessionId, selectedPlanId]);

  // Auto-execute when a plan is selected
  useEffect(() => {
    if (
      selectedPlan &&
      selectedPlan.status !== ACTION_PLAN_STATUSES.STARTED &&
      selectedPlan.status !== ACTION_PLAN_STATUSES.COMPLETED &&
      selectedPlan.status !== ACTION_PLAN_STATUSES.FAILED &&
      !isLoading
    ) {
      navigateThroughPlan();
    }
  }, [selectedPlan]);

  // Add useEffect to scroll when logs change
  useEffect(() => {
    const scrollToBottom = () => {
      const parentDiv = document.querySelector('#scrollable-panel-content');
      if (parentDiv) {
        // Add a small delay to ensure content is rendered
        setTimeout(() => {
          parentDiv.scrollTo({
            top: parentDiv.scrollHeight + 1000, // Add extra padding to ensure we reach bottom
            behavior: 'smooth',
          });
        }, 100);
      }
    };

    if (selectedPlan?.logs?.length) {
      scrollToBottom();
    }
  }, [selectedPlan?.logs]);

  if (!selectedPlan) {
    return null;
  }

  return (
    <div className='d-flex justify-content-center w-100'>
      {selectedPlan.status !== ACTION_PLAN_STATUSES.COMPLETED ? (
        <div
          className='mt-1 alert alert-warning'
          style={{ maxWidth: '750px', width: '100%' }}
        >
          <div className='d-flex justify-content-center'>
            <div className='d-flex align-items-center gap-3'>
              <div className='fw-semibold'>
                Workflow: <b>{selectedPlan.status}</b>
              </div>

              {/* Do not remove, needed when testing */}
              {/* <Button
                onClick={() => setShowActionDetails(!showActionDetails)}
                className='btn btn-link p-0 text-decoration-none px-2'
                variant='falcon-default'
                size='sm'
              >
                View Actions Plan Details
              </Button> */}

              {isLoading ? (
                <div>Executing Action Plan...</div>
              ) : (
                <Button
                  onClick={navigateThroughPlan}
                  variant='falcon-warning'
                  size='sm'
                  className='btn btn-link p-0 text-decoration-none px-2'
                >
                  Restart
                </Button>
              )}
            </div>
          </div>

          {showActionDetails && (
            <ActionDetails actions={selectedPlan.sherlockActions} />
          )}

          {showExecutionLogs && <ExecutionLogs logs={selectedPlan.logs} />}
        </div>
      ) : null}
    </div>
  );
};

export default ActionPlanViewer;

